// Le cerveau : les notes par dossier, la recherche plein texte (accents ignorés), et une note en lecture
// ou en édition. Le texte est édité tel quel, frontmatter compris : c'est ce que le miroir écrit dans ~/brain.

import { useEffect, useMemo, useState } from 'react';

import type { Actions } from '../App';
import { EnTeteRetour, Fenetre, Nav } from '../components';
import { Markdown } from '../Markdown';
import { NonConnecte, type Note, type ResumeNote, api } from '../lib/api';
import { avecWikiliens, corpsSansFrontmatter, proprietes } from '../lib/note';

const TYPES: [string, string][] = [
  ['profil', 'Profil'], ['personne', 'Personnes'], ['habitude', 'Habitudes'], ['sante', 'Santé et sport'],
  ['projet', 'Projets'], ['journal', 'Journal'], ['maison', 'Maison'], ['libre', 'Divers'],
];

function versConnexion(e: unknown): boolean {
  if (e instanceof NonConnecte) {
    window.location.hash = '#/connexion';
    return true;
  }
  return false;
}

export function Cerveau({ sante }: { sante: boolean }) {
  const [notes, setNotes] = useState<ResumeNote[] | null>(null);
  const [q, setQ] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [nouvelle, setNouvelle] = useState<{ type: string; titre: string } | null>(null);

  useEffect(() => {
    // La recherche part après une courte pause de frappe.
    const t = window.setTimeout(() => {
      api.notes(q.trim() || undefined).then(setNotes).catch((e: unknown) => !versConnexion(e) && setErreur((e as Error).message));
    }, q ? 250 : 0);
    return () => window.clearTimeout(t);
  }, [q]);

  const parType = useMemo(() => {
    const m = new Map<string, ResumeNote[]>();
    for (const n of notes ?? []) m.set(n.type, [...(m.get(n.type) ?? []), n]);
    return m;
  }, [notes]);

  const creer = async () => {
    if (!nouvelle?.titre.trim()) return;
    try {
      const n = await api.creerNote(nouvelle.type, nouvelle.titre.trim());
      window.location.hash = `#/cerveau/${n.id}`;
    } catch (e) {
      if (!versConnexion(e)) setErreur((e as Error).message);
    }
  };

  return (
    <main className="ecran avec-nav">
      <EnTeteRetour titre="Cerveau" droite={notes ? `${notes.length}` : ''} />
      <input className="saisie texte" type="search" placeholder="Chercher dans le cerveau…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Chercher" />
      {erreur && <p className="bandeau-erreur" style={{ margin: 0 }}>{erreur}</p>}

      {nouvelle ? (
        <Fenetre>
          <select className="saisie texte" value={nouvelle.type} onChange={(e) => setNouvelle({ ...nouvelle, type: e.target.value })} aria-label="Dossier">
            {TYPES.map(([t, l]) => <option key={t} value={t}>{l}</option>)}
          </select>
          <input className="saisie texte" placeholder="Titre" autoFocus value={nouvelle.titre} onChange={(e) => setNouvelle({ ...nouvelle, titre: e.target.value })} />
          <div className="boutons">
            <button className="btn principal" onClick={() => void creer()}>Créer</button>
            <button className="btn" onClick={() => setNouvelle(null)}>Annuler</button>
          </div>
        </Fenetre>
      ) : (
        <button className="btn" onClick={() => setNouvelle({ type: 'libre', titre: '' })}>Nouvelle note</button>
      )}

      {notes === null && <p className="message muted">Chargement…</p>}
      {notes?.length === 0 && <p className="message muted">{q ? 'Aucune note ne correspond.' : 'Le cerveau est vide.'}</p>}
      {TYPES.filter(([t]) => parType.has(t)).map(([t, libelle]) => (
        <section key={t} className="dossier">
          <h2 className="dossier-titre">{libelle}<span>{parType.get(t)!.length}</span></h2>
          <div className="dossier-notes">
            {parType.get(t)!.map((n) => (
              <a key={n.id} className="note-ligne" href={`#/cerveau/${n.id}`}>
                <span className="t">{n.titre}</span>
                <span className="mono muted">{n.chemin.split('/').pop()}</span>
              </a>
            ))}
          </div>
        </section>
      ))}
      <Nav actif="cerveau" sante={sante} />
    </main>
  );
}

export function NoteEcran({ id, actions }: { id: string; actions: Actions }) {
  const [note, setNote] = useState<Note | null>(null);
  const [liens, setLiens] = useState<{ cible: string; id: string | null }[]>([]);
  const [brouillon, setBrouillon] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    api.note(id).then(setNote).catch((e: unknown) => !versConnexion(e) && setErreur((e as Error).message));
    // Les wikiliens se résolvent à part : une note sans lien s'affiche sans attendre.
    api.liensNote(id).then(setLiens).catch(() => setLiens([]));
  }, [id]);

  const lecture = useMemo(() => {
    if (!note) return null;
    return { proprietes: proprietes(note.frontmatter), corps: avecWikiliens(corpsSansFrontmatter(note.contenu), liens) };
  }, [note, liens]);

  const enregistrer = async () => {
    if (!note || brouillon === null) return;
    try {
      // `seqAttendu` : si la note a changé ailleurs (Claude Code, un autre appareil), rien n'est écrasé.
      setNote(await api.modifierNote(note.id, brouillon, note.seq));
      setBrouillon(null);
      actions.toast({ texte: 'Note enregistrée.' });
    } catch (e) {
      if (!versConnexion(e)) actions.toast({ texte: `${(e as Error).message} : recharge la note avant de modifier.`, erreur: true });
    }
  };

  const supprimer = async () => {
    if (!note || !window.confirm(`Supprimer « ${note.titre} » ? Elle reste récupérable 30 jours.`)) return;
    try {
      await api.supprimerNote(note.id);
      window.location.hash = '#/cerveau';
    } catch (e) {
      if (!versConnexion(e)) actions.toast({ texte: (e as Error).message, erreur: true });
    }
  };

  return (
    <main className="ecran">
      <EnTeteRetour titre={note?.titre ?? 'Note'} retour="#/cerveau" />
      {erreur && <p className="bandeau-erreur">{erreur}</p>}
      {note && (
        <>
          <span className="mono muted" style={{ fontSize: 11 }}>{note.chemin} · modifiée le {new Date(note.majLe).toLocaleString('fr-FR')}</span>
          {brouillon === null ? (
            <>
              {lecture && lecture.proprietes.length > 0 && (
                <dl className="proprietes-note">
                  {lecture.proprietes.map(([cle, valeur]) => (
                    <div key={cle}><dt>{cle}</dt><dd>{valeur}</dd></div>
                  ))}
                </dl>
              )}
              {/* Un .yaml (le programme) n'est pas du Markdown : il reste en texte, police à chasse fixe. */}
              <Fenetre>
                {note.chemin.endsWith('.md') && lecture
                  ? <div className="note-md"><Markdown texte={lecture.corps} /></div>
                  : <div className="texte-note mono">{note.contenu}</div>}
              </Fenetre>
              <div className="boutons">
                <button className="btn principal" onClick={() => setBrouillon(note.contenu)}>Modifier</button>
                <button className="btn" onClick={() => void supprimer()}>Supprimer</button>
              </div>
            </>
          ) : (
            <>
              <textarea className="saisie texte editeur-note" value={brouillon} onChange={(e) => setBrouillon(e.target.value)} aria-label="Contenu de la note" />
              <div className="boutons">
                <button className="btn principal" onClick={() => void enregistrer()}>Enregistrer</button>
                <button className="btn" onClick={() => setBrouillon(null)}>Annuler</button>
              </div>
            </>
          )}
        </>
      )}
    </main>
  );
}
